import React, { useEffect, useState, useRef } from 'react';
import $ from 'jquery';
import { Icon } from '@iconify/react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import {
  getPlanningGroups,
  createPlanningGroup,
  updatePlanningGroup,
  deletePlanningGroup,
} from '../api/planning-groupAPI';
import { fetchData } from '../utils/apiHelpers';

const PlanningGroupListLayer = () => {
  const userProfile = useUserProfile();
  const [groupList, setGroupList] = useState([]);
  const [newGroup, setNewGroup] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});

  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const fetchGroups = () => {
    fetchData(getPlanningGroups, {}, setGroupList);
  };


  const validateForm = () => {
    const newErrors = {};
    if (!newGroup.planningGroupName?.trim()) newErrors.planningGroupName = 'Name is required';
    if (!newGroup.planningGroupCode?.trim()) newErrors.planningGroupCode = 'Code is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
    if (groupList.length > 0) {
      dtRef.current = $(tableRef.current).DataTable({
        pageLength: 25,
        dom: 'rtp',
        responsive: false,
        language: { paginate: { first: '', last: '', next: '', previous: '' } },
      });
    }
    return () => {
      if (dtRef.current) {
        dtRef.current.destroy(true);
        dtRef.current = null;
      }
    };
  }, [groupList]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.search(searchTerm).draw();
    }
  }, [searchTerm]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewGroup((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = (item) => {
    setNewGroup(item);
    setEditingId(item.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this planning group?')) return;
    try {
      await deletePlanningGroup(id);
      if (dtRef.current) { dtRef.current.destroy(); dtRef.current = null; }
      await fetchGroups();
    } catch (error) {
      console.error('Error deleting planning group:', error);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      const payload = {};
      for (const key in newGroup) {
        if (newGroup[key] !== null && typeof newGroup[key] !== 'object') {
          payload[key] = newGroup[key];
        }
      }
      if (editingId) {
        await updatePlanningGroup(editingId, payload);
      } else {
        await createPlanningGroup(payload);
      }
      setShowModal(false);
      setEditingId(null);
      if (dtRef.current) { dtRef.current.destroy(); dtRef.current = null; }
      setNewGroup({});
      await fetchGroups();
    } catch (error) {
      console.error('Error saving planning group:', error);
    }
  };

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header d-flex justify-content-between align-items-center p-3">
        <input
          type="text"
          className="form-control w-auto"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {userProfile?.privileges?.includes('planning-group:create') && (
          <button
            className="btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2"
            onClick={() => { setNewGroup({}); setEditingId(null); setShowModal(true); }}
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl" />
            Add Planning Group
          </button>
        )}
      </div>

      <div className="card-body p-0">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table mb-0 w-100 rounded-0" ref={tableRef}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {groupList.map((item) => (
                <tr key={item.id}>
                  <td><strong>{item.planningGroupName}</strong></td>
                  <td>{item.planningGroupCode}</td>
                  <td>
                    <div className="d-flex gap-2">
                      {userProfile?.privileges?.includes('planning-group:update') && (
                        <button
                          className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleEdit(item)}
                        >
                          <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                        </button>
                      )}
                      {userProfile?.privileges?.includes('planning-group:delete') && (
                        <button
                          className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleDelete(item.id)}
                        >
                          <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Planning Group' : 'New Planning Group'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Planning Group Name *</label>
                    <input
                      className={`form-control ${errors.planningGroupName ? 'is-invalid' : ''}`}
                      name="planningGroupName"
                      value={newGroup.planningGroupName || ''}
                      onChange={handleInputChange}
                    />
                    {errors.planningGroupName && <div className="invalid-feedback">{errors.planningGroupName}</div>}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Planning Group Code *</label>
                    <input
                      className={`form-control ${errors.planningGroupCode ? 'is-invalid' : ''}`}
                      name="planningGroupCode"
                      value={newGroup.planningGroupCode || ''}
                      onChange={handleInputChange}
                    />
                    {errors.planningGroupCode && <div className="invalid-feedback">{errors.planningGroupCode}</div>}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
                <button className="btn btn-primary" onClick={handleSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlanningGroupListLayer;
